import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createCommandAsyncThunk,
  createQueryAsyncThunk,
} from "../../app/createApiAsyncThunk";
import { RootState } from "../../app/store";
import {
  addUserMessageRecipientsCmd,
  createUserMessageCmd,
  getAttachmentDownloadUrlQry,
  getAttachmentUploadUrlQry,
  getUserMessageByIdQry,
  getUserMessagesQry,
  startUserMessageProcessingCmd,
  uploadUserMessageAttachmentToS3,
} from "./userMessagesAPI";
import {
  AttachmentDownloadUrlParams,
  AttachmentDownloadUrlResponse,
  AttachmentUploadUrlParams,
  AttachmentUploadUrlResponse,
  UserMessageByIdParams,
  UserMessageByIdResponse,
  UserMessagesQueryParams,
  UserMessagesResponse,
} from "./userMessagesTypes";

type SendStatus = "idle" | "pending" | "succeeded" | "failed";

interface UserMessagesState {
  sendStatus: SendStatus;
}

const initialState: UserMessagesState = {
  sendStatus: "idle",
};

export const createUserMessage = createCommandAsyncThunk(
  "userMessages/createUserMessage",
  createUserMessageCmd,
);

export const addUserMessageRecipients = createCommandAsyncThunk(
  "userMessages/addUserMessageRecipients",
  addUserMessageRecipientsCmd,
);

export const startUserMessageProcessing = createCommandAsyncThunk(
  "userMessages/startUserMessageProcessing",
  startUserMessageProcessingCmd,
);

export const fetchUserMessages = createQueryAsyncThunk<
  UserMessagesResponse,
  UserMessagesQueryParams
>("userMessages/fetchUserMessages", getUserMessagesQry);

export const fetchUserMessageById = createQueryAsyncThunk<
  UserMessageByIdResponse,
  UserMessageByIdParams
>("userMessages/fetchUserMessageById", getUserMessageByIdQry);

export const fetchAttachmentUploadUrl = createQueryAsyncThunk<
  AttachmentUploadUrlResponse,
  AttachmentUploadUrlParams
>("userMessages/fetchAttachmentUploadUrl", getAttachmentUploadUrlQry);

export const uploadUserMessageAttachment = createAsyncThunk<
  void,
  { url: string; file: File },
  { state: RootState; rejectValue: string }
>(
  "userMessages/uploadUserMessageAttachment",
  async ({ url, file }, { rejectWithValue }) => {
    try {
      await uploadUserMessageAttachmentToS3(url, file);
    } catch {
      return rejectWithValue("attachment_upload_failed");
    }
  },
);

export const fetchAttachmentDownloadUrl = createQueryAsyncThunk<
  AttachmentDownloadUrlResponse,
  AttachmentDownloadUrlParams
>("userMessages/fetchAttachmentDownloadUrl", getAttachmentDownloadUrlQry);

export const userMessagesSlice = createSlice({
  name: "userMessages",
  initialState,
  reducers: {
    resetSendStatus: (state) => {
      state.sendStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createUserMessage.pending, (state) => {
        state.sendStatus = "pending";
      })
      .addCase(createUserMessage.rejected, (state) => {
        state.sendStatus = "failed";
      })
      .addCase(addUserMessageRecipients.rejected, (state) => {
        state.sendStatus = "failed";
      })
      .addCase(fetchAttachmentUploadUrl.rejected, (state) => {
        state.sendStatus = "failed";
      })
      .addCase(uploadUserMessageAttachment.rejected, (state) => {
        state.sendStatus = "failed";
      })
      .addCase(startUserMessageProcessing.fulfilled, (state) => {
        state.sendStatus = "succeeded";
      })
      .addCase(startUserMessageProcessing.rejected, (state) => {
        state.sendStatus = "failed";
      });
  },
});

export const { resetSendStatus } = userMessagesSlice.actions;
export const userMessagesReducer = userMessagesSlice.reducer;
